import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button, Modal } from '@weezevent/nacre';
import GalleryModal from '../../../components/gallery';
import './add-favicon.scss';

function FaviconModalContent({ setOpenFaviconModal, errors, fileErrors, url, favicon }) {
    const { t } = useTranslation();
    return (
        <div>
            <div className="favicon-description">{t('ticket.event.minisite.other_options.favicon_modal.description')}</div>
            <div>
                <div className="favicon-title">Favicon</div>
                {favicon ? (
                    <>
                        <div className="favicon-container">
                            <img alt="event logo" className="preview-favicon" src={favicon} />
                        </div>
                        <div className="favicon-cta">
                            <div className="favicon-change" onClick={() => setOpenFaviconModal(true)}>
                                {t('ticket.event.minisite.other_options.favicon_modal.change')}
                            </div>
                        </div>
                    </>
                ) : (
                    <Button onClick={() => setOpenFaviconModal(true)}>{t('ticket.event.minisite.other_options.favicon_modal.add_image')}</Button>
                )}
                {fileErrors.map(error => {
                    return (
                        <div className={'dropzone-error-over-limit'} key={error}>
                            {errors[error]}
                        </div>
                    );
                })}
            </div>
            <div>
                <div className="favicon-preview">{t('ticket.event.minisite.other_options.favicon_modal.preview')}</div>
                <div className="preview-container">
                    <div className="preview-top">
                        <div className="circles">
                            <div className="circle"></div>
                            <div className="circle"></div>
                            <div className="circle"></div>
                        </div>
                        <div className="tab">
                            <div className="img" style={{ backgroundImage: 'url("' + favicon + '")', backgroundSize: '20px' }}></div>
                            <div className="title"></div>
                        </div>
                        <div className="plus"></div>
                    </div>
                    <div className="preview-bottom">
                        <div className="squares">
                            <div className="square"></div>
                            <div className="square"></div>
                            <div className="square"></div>
                        </div>
                        <div className="url-container">
                            <div className="url">https://{url}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

FaviconModalContent.propTypes = {
    url: PropTypes.string,
    favicon: PropTypes.string,
    setFavicon: PropTypes.func,
    setOpenFaviconModal: PropTypes.func,
    errors: PropTypes.object,
    fileErrors: PropTypes.object
};

export default function AddFavicon({
    organization,
    removeFavicon = () => {},
    onUpload = () => {},
    onCancel = () => {},
    openModal = false,
    image = null,
    url = '',
    maxSize = 5242880
}) {
    const [isOpenModal, setOpenModal] = React.useState(openModal);
    const [isOpenFaviconModal, setOpenFaviconModal] = React.useState(false);
    const [favicon, setFavicon] = React.useState(image);

    const { t } = useTranslation();

    const errors = {
        'file-invalid-type': t('ticket.event.dropzone_error_file_invalid_type'),
        'file-too-large': t('ticket.event.dropzone_error_file_too_large', { maxSize: Math.trunc(maxSize / 1000000) }),
        'file-too-small': t('ticket.event.dropzone_error_file_too_small'),
        'too-many-files': t('ticket.event.dropzone_error_too_many_files')
    };

    React.useEffect(() => setOpenModal(openModal), [openModal]);

    const onSelected = () => {
        setOpenModal(false);

        favicon ? onUpload(favicon) : removeFavicon();
    };

    const onSelectedFavicon = data => {
        setOpenFaviconModal(false);
        setFavicon(data);
    };

    const onClose = () => {
        setOpenModal(false);
        setFavicon(image);
        onCancel();
    };
    const onCloseFavicon = () => {
        setOpenFaviconModal(false);
        setFavicon(image);
        onCancel();
    };

    return (
        <React.Fragment>
            <Modal
                size="large"
                title="Favicon"
                open={Boolean(isOpenModal)}
                allowExternalClosing={false}
                onClose={onClose}
                cancelButton={<Button onClick={onClose} label={t('ticket.common.action.cancel')} />}
                submitButton={<Button primary disabled={image === favicon} label={t('ticket.common.action.save')} onClick={onSelected} />}
            >
                <FaviconModalContent errors={errors} fileErrors={[]} url={url} favicon={favicon} setOpenFaviconModal={setOpenFaviconModal} />
            </Modal>
            <GalleryModal
                apiKeyUnsplash={window.weezConfig.unsplash}
                onSelected={onSelectedFavicon}
                onUpload={onSelectedFavicon}
                organization={organization}
                isOpenModal={isOpenFaviconModal}
                onClose={onCloseFavicon}
                maxSize={maxSize}
            />
            <div className="add-favicon-container">
                {image ? (
                    <>
                        <img alt="favicon" src={image} className="add-favicon-image" />
                        <div className="dropzone-container-cta">
                            <div className="dropzone-button" onClick={() => setOpenModal(true)}>
                                {t('ticket.common.action.edit')}
                            </div>
                            <div className="dropzone-button-separator">|</div>
                            <div
                                className="dropzone-button"
                                onClick={() => {
                                    setFavicon(null);
                                    removeFavicon();
                                }}
                            >
                                {t('ticket.common.action.delete')}
                            </div>
                        </div>
                    </>
                ) : (
                    <Button onClick={() => setOpenModal(true)}>{t('ticket.common.action.add')}</Button>
                )}
            </div>
        </React.Fragment>
    );
}

AddFavicon.propTypes = {
    organization: PropTypes.object.isRequired,
    onUpload: PropTypes.func,
    removeFavicon: PropTypes.func,
    fileTypes: PropTypes.array,
    onCancel: PropTypes.func,
    image: PropTypes.string
};

AddFavicon.fileTypes = ['jpg', 'jpeg', 'png', 'gif'];
